import type { CommonRequestFn } from "@marketplace/http";

import { ENDPOINTS } from "@/constants/endpoints";
import { httpClient, httpClientNoApiVersion } from "@/http";

import type {
  CalculateClaimEstimationRequestDto,
  CalculateClaimEstimationResponseDto
} from "../dtos/calculate-claim-estimation-dto";
import type {
  DeleteClaimRequestDto,
  DeleteClaimResponseDto
} from "../dtos/delete-claim-dto";
import type { ExportOrderRequestDto } from "../dtos/export-order-request-dto";
import type { GetBadgeCountResponseDto } from "../dtos/get-badge-count-response-dto";
import type { GetClaimHistoryDetailResponseDto } from "../dtos/get-claim-history-detail-response-dto";
import type { GetClaimHistoryListRequestDto } from "../dtos/get-claim-history-list-request-dto";
import type { GetClaimHistoryListResponseDto } from "../dtos/get-claim-history-list-response-dto";
import type { GetClaimIntegrationStatusRequestDto } from "../dtos/get-claim-integration-status-request-dto";
import type { GetClaimOrderListRequestDto } from "../dtos/get-claim-order-list-request-dto";
import type { GetClaimOrderListResponseDto } from "../dtos/get-claim-order-list-response-dto";
import type { GetClaimTrackingStatusResponseDto } from "../dtos/get-claim-tracking-status-response-dto";
import type { GetErfinaInvoiceStatusResponseDto } from "../dtos/get-erfina-invoice-status-response-dto";
import type { GetIntegrationStatusRequestDto } from "../dtos/get-integration-status-request-dto";
import type { GetIntegrationStatusResponseDto } from "../dtos/get-integration-status-response-dto";
import type { GetOrderCountRequestDto } from "../dtos/get-order-count-request-dto";
import type { GetOrderCountResponseDto } from "../dtos/get-order-count-response-dto";
import type { GetOrderDetailRequestDto } from "../dtos/get-order-detail-request-dto";
import type { GetOrderDetailResponseDto } from "../dtos/get-order-detail-response-dto";
import type { GetOrderListRequestDto } from "../dtos/get-order-list-requets-dto";
import type { GetOrderListResponseDto } from "../dtos/get-order-list-response-dto";
import type { GetPayoutStatusRequestDto } from "../dtos/get-payout-status-request-dto";
import type { GetPayoutStatusResponseDto } from "../dtos/get-payout-status-response-dto";
import type { GetRecurringOrderListRequestDto } from "../dtos/get-recurring-order-list-request-dto";
import type { GetRecurringOrderListResponseDto } from "../dtos/get-recurring-order-list-response-dto";
import type { GetTransactionHistoryListRequestDto } from "../dtos/get-transaction-history-list-request-dto";
import type { GetTransactionHistoryListResponseDto } from "../dtos/get-transaction-history-list-response-dto";
import type { PostRetryFailedClaimIntegrationStepRequestDto } from "../dtos/post-retry-failed-claim-integration-step-request-dto";
import type { PostRetryFailedIntegrationStepRequestDto } from "../dtos/post-retry-failed-integration-step-request-dto";
import type { PostRetryFailedIntegrationStepResponseDto } from "../dtos/post-retry-failed-integration-step-response-dto";
import type { SendCredentialsRequestDto } from "../dtos/send-credentials-request-dto";
import type { SendCredentialsResponseDto } from "../dtos/send-credentials-response-dto";
import type {
  SubmitClaimOrderRequestDto,
  SubmitClaimOrderResponseDto
} from "../dtos/submit-claim-order-dto";
import type {
  UpdateOrderClaimStatusRequestDto,
  UpdateOrderClaimStatusResponseDto
} from "../dtos/update-order-claim-status-dto";

type OrderServiceContract = {
  getOrderList: CommonRequestFn<
    GetOrderListRequestDto,
    GetOrderListResponseDto
  >;
  getRecurringOrderList: CommonRequestFn<
    GetRecurringOrderListRequestDto,
    GetRecurringOrderListResponseDto
  >;
  getTransactionHistoryList: CommonRequestFn<
    GetTransactionHistoryListRequestDto,
    GetTransactionHistoryListResponseDto
  >;
  getOrderCount: CommonRequestFn<
    Partial<GetOrderCountRequestDto>,
    GetOrderCountResponseDto
  >;
  getOrderDetail: CommonRequestFn<
    GetOrderDetailRequestDto,
    GetOrderDetailResponseDto
  >;
  sendCredentials: CommonRequestFn<
    SendCredentialsRequestDto,
    SendCredentialsResponseDto
  >;
  getIntegrationStatus: CommonRequestFn<
    GetIntegrationStatusRequestDto,
    GetIntegrationStatusResponseDto
  >;
  getPayoutStatus: CommonRequestFn<
    GetPayoutStatusRequestDto,
    GetPayoutStatusResponseDto
  >;
  getClaimIntegrationStatus: CommonRequestFn<
    GetClaimIntegrationStatusRequestDto,
    GetIntegrationStatusResponseDto
  >;
  postRetryFailedIntegrationStep: CommonRequestFn<
    PostRetryFailedIntegrationStepRequestDto,
    PostRetryFailedIntegrationStepResponseDto
  >;
  postRetryFailedClaimIntegrationStep: CommonRequestFn<
    PostRetryFailedClaimIntegrationStepRequestDto,
    PostRetryFailedIntegrationStepResponseDto
  >;
  getClaimOrderList: CommonRequestFn<
    GetClaimOrderListRequestDto,
    GetClaimOrderListResponseDto
  >;
  getClaimOrderBadgeCount: () => Promise<GetBadgeCountResponseDto>;
  submitClaimOrder: CommonRequestFn<
    SubmitClaimOrderRequestDto,
    SubmitClaimOrderResponseDto
  >;
  getClaimHistoryList: CommonRequestFn<
    GetClaimHistoryListRequestDto,
    GetClaimHistoryListResponseDto
  >;
  getClaimHistoryBadgeCount: () => Promise<GetBadgeCountResponseDto>;
  getClaimHistoryDetail: CommonRequestFn<
    { claimId: string },
    GetClaimHistoryDetailResponseDto
  >;
  approveClaim: CommonRequestFn<
    UpdateOrderClaimStatusRequestDto,
    UpdateOrderClaimStatusResponseDto
  >;
  rejectClaim: CommonRequestFn<
    UpdateOrderClaimStatusRequestDto,
    UpdateOrderClaimStatusResponseDto
  >;
  getClaimStatusTracking: CommonRequestFn<
    { claimId: string },
    GetClaimTrackingStatusResponseDto
  >;
  getErfinaInvoiceStatus: CommonRequestFn<
    { publicClaimId: string },
    GetErfinaInvoiceStatusResponseDto
  >;
  calculateClaimEstimation: CommonRequestFn<
    CalculateClaimEstimationRequestDto,
    CalculateClaimEstimationResponseDto
  >;
  deleteClaim: CommonRequestFn<DeleteClaimRequestDto, DeleteClaimResponseDto>;
  exportOrderList: (payload: ExportOrderRequestDto) => Promise<Blob>;
};

export const OrderService: OrderServiceContract = {
  getOrderList: ({ payload, options }) => {
    const { order_status } = payload;
    let orderStatusToSend: string = order_status;

    if (order_status === "all") orderStatusToSend = "";

    return httpClient
      .get(ENDPOINTS.ORDER.LIST, {
        ...options,
        searchParams: {
          ...payload,
          order_status: orderStatusToSend,
          sort: "newest"
        }
      })
      .json<GetOrderListResponseDto>();
  },

  getRecurringOrderList: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER.RECURRING_ORDERS, {
        ...options,
        searchParams: {
          ...payload,
          sort: payload.sort ?? "newest"
        }
      })
      .json<GetRecurringOrderListResponseDto>();
  },

  getTransactionHistoryList: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER.TRANSACTION_HISTORY, {
        ...options,
        searchParams: {
          ...payload,
          sort: payload.sort ?? "newest"
        }
      })
      .json<GetTransactionHistoryListResponseDto>();
  },

  getOrderCount: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER.COUNT, {
        ...options,
        searchParams: {
          ...payload
        }
      })
      .json<GetOrderCountResponseDto>();
  },

  getOrderDetail: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER.DETAIL(payload.orderId), {
        ...options
      })
      .json<GetOrderDetailResponseDto>();
  },

  sendCredentials: ({ payload, options }) => {
    return httpClient
      .put(ENDPOINTS.ORDER.SEND_ACCESS(payload.orderItemId), {
        ...options,
        json: {
          credentials: payload.credentials,
          pdf_documentation: 2
        }
      })
      .json();
  },

  getIntegrationStatus: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER.INTEGRATION_STATUS(payload.orderId), {
        ...options
      })
      .json<GetIntegrationStatusResponseDto>();
  },

  getPayoutStatus: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER.PAYOUT_STATUS(payload.orderId), {
        ...options
      })
      .json<GetPayoutStatusResponseDto>();
  },

  getClaimIntegrationStatus: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER_CLAIMS.INTEGRATION_STATUS(payload.public_id), {
        ...options
      })
      .json<GetIntegrationStatusResponseDto>();
  },

  postRetryFailedIntegrationStep: ({ payload, options }) => {
    const { orderId, stepName, stepIndex } = payload;
    const endpoint =
      stepIndex === 0
        ? ENDPOINTS.ORDER.RETRY_INTEGRATION_STEP(orderId)
        : ENDPOINTS.ORDER.RETRY_INTEGRATION_STEP(orderId, stepName);

    return httpClientNoApiVersion
      .post(endpoint, {
        ...options
      })
      .json<PostRetryFailedIntegrationStepResponseDto>();
  },

  postRetryFailedClaimIntegrationStep: ({ payload, options }) => {
    const { claimId, stepName } = payload;

    return httpClientNoApiVersion
      .post(ENDPOINTS.ORDER_CLAIMS.RETRY_INTEGRATION_STEP(claimId, stepName), {
        ...options
      })
      .json<PostRetryFailedIntegrationStepResponseDto>();
  },

  getClaimOrderList: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER.READY_TO_CLAIM, {
        ...options,
        searchParams: {
          ...payload
        }
      })
      .json<GetClaimOrderListResponseDto>();
  },

  getClaimOrderBadgeCount: async () =>
    httpClient
      .get(ENDPOINTS.ORDER.READY_TO_CLAIM_BADGE_COUNTS)
      .json<GetBadgeCountResponseDto>(),

  getClaimHistoryList: ({ payload, options }) => {
    const { status, start_date, end_date, ...restPayload } = payload;

    return httpClient
      .get(ENDPOINTS.ORDER_CLAIMS.LIST, {
        ...options,
        searchParams: {
          ...restPayload,
          start_date: start_date ?? undefined,
          end_date: end_date ?? undefined,
          ...(status && status !== "all" ? { status } : {})
        }
      })
      .json<GetClaimHistoryListResponseDto>();
  },

  getClaimHistoryBadgeCount: async () =>
    httpClient
      .get(ENDPOINTS.ORDER_CLAIMS.BADGE_COUNTS)
      .json<GetBadgeCountResponseDto>(),

  getClaimHistoryDetail: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER_CLAIMS.DETAIL(payload.claimId), {
        ...options
      })
      .json<GetClaimHistoryDetailResponseDto>();
  },

  approveClaim: ({ payload, options }) => {
    return httpClient
      .put(ENDPOINTS.ORDER_CLAIMS.UPDATE_STATUS(payload.claimId), {
        ...options,
        json: {
          status: payload.status
        }
      })
      .json<UpdateOrderClaimStatusResponseDto>();
  },

  rejectClaim: ({ payload, options }) => {
    return httpClient
      .put(ENDPOINTS.ORDER_CLAIMS.UPDATE_STATUS(payload.claimId), {
        ...options,
        json: {
          status: payload.status,
          reason: payload.reason
        }
      })
      .json<UpdateOrderClaimStatusResponseDto>();
  },

  submitClaimOrder: ({ payload, options }) => {
    const body = {
      bank_account_id: payload.bank_account_id,
      invoice_number: payload.invoice_number,
      invoice_file_path: payload.invoice_file_path,
      invoice_amount: payload.invoice_amount,
      invoice_date: payload.invoice_date,
      tax_invoice_number: payload.tax_invoice_number,
      orders: payload.orders
    };
    if (payload.claim_id) {
      return httpClient
        .put(ENDPOINTS.ORDER_CLAIMS.RESUBMIT(payload.claim_id), {
          ...options,
          json: body
        })
        .json<SubmitClaimOrderResponseDto>();
    }
    return httpClient
      .post(ENDPOINTS.ORDER_CLAIMS.SUBMIT, {
        ...options,
        json: body
      })
      .json<SubmitClaimOrderResponseDto>();
  },

  getClaimStatusTracking: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.ORDER_CLAIMS.STATUS_TRACKING(payload.claimId), {
        ...options
      })
      .json<GetClaimTrackingStatusResponseDto>();
  },

  getErfinaInvoiceStatus: ({ payload, options }) => {
    return httpClient
      .get(
        ENDPOINTS.ORDER_CLAIMS.ERFINA_STATUS_TRACKING(payload.publicClaimId),
        {
          ...options
        }
      )
      .json<GetErfinaInvoiceStatusResponseDto>();
  },

  calculateClaimEstimation: ({ payload, options }) => {
    return httpClient
      .post(ENDPOINTS.ORDER_CLAIMS.CALCULATE_ESTIMATION, {
        ...options,
        json: {
          order_ids: payload.order_ids
        }
      })
      .json<CalculateClaimEstimationResponseDto>();
  },

  deleteClaim: ({ payload, options }) => {
    return httpClient
      .delete(ENDPOINTS.ORDER_CLAIMS.DELETE(payload.claimId), {
        ...options
      })
      .json<DeleteClaimResponseDto>();
  },

  exportOrderList: async (payload: ExportOrderRequestDto) => {
    return httpClient
      .post(ENDPOINTS.ORDER.EXPORT, {
        json: payload
      })
      .blob();
  }
};
